/* Architect — verbatim ports of the vanilla SPA helpers + constants.
   The cleanSpec / cleanNode pruning MUST stay exact: the server
   validator rejects specs that still carry empty strings or stray
   type-specific fields. */

/* ── constants ─────────────────────────────────────────── */

export interface ModelDef {
  value: string;
  label: string;
}

// Wire values are the immutable API contract; the poster surface only
// offers two tiers: Medium (claude-opus-4-8) and Ultra (claude-fable-5).
export const MODELS: ModelDef[] = [
  { value: 'claude-opus-4-8', label: 'Medium' },
  { value: 'claude-fable-5', label: 'Ultra' },
];

// Fallback label for the prompt busy line when the model is unknown.
export const FALLBACK_MODEL_LABEL = 'Ultra';

export function modelLabel(value: string): string {
  const m = MODELS.find((x) => x.value === value);
  return m ? m.label : FALLBACK_MODEL_LABEL;
}

/* ── model white-label (never echo a raw id to the user) ──────────
   Two surfaces speak different tier names for the SAME wire ids:
   the poster offers Medium/Ultra, the estimator offers Lite/Max. Both
   map any other Anthropic family to its friendly name and fall back to
   a neutral 'AI' for anything unrecognised — a raw id must never leak
   into visible text. null/'' collapse to '' so callers can guard. */

/** POSTER-surface label: opus → Medium, fable → Ultra, sonnet → Sonnet,
    haiku → Haiku, gpt-* → Max, ''/null → '', anything else → 'AI'. */
export function modelDisplay(value?: string | null): string {
  if (value == null) return '';
  const v = value.trim().toLowerCase();
  if (!v) return '';
  if (v.includes('fable')) return 'Ultra';
  if (v.includes('opus')) return 'Medium';
  if (v.includes('sonnet')) return 'Sonnet';
  if (v.includes('haiku')) return 'Haiku';
  if (v.startsWith('gpt')) return 'Max';
  return 'AI';
}

/** ESTIMATE-surface label: the two tiers read Lite (claude-opus-4-8) and
    Max (gpt-*). fable → Ultra for completeness; ''/null → ''; else 'AI'. */
export function modelDisplayEstimate(value?: string | null): string {
  if (value == null) return '';
  const v = value.trim().toLowerCase();
  if (!v) return '';
  if (v.includes('opus')) return 'Lite';
  if (v.startsWith('gpt')) return 'Max';
  if (v.includes('fable')) return 'Ultra';
  return 'AI';
}

export const PALETTE: Record<string, string> = {
  navy: '#1a2445',
  blue: '#4f8bae',
  purple: '#9438c4',
  green: '#067429',
  teal: '#2da6b0',
  pink: '#e42f8a',
  eblue: '#0b5abc',
};

export const SOURCE_EN: Record<string, string> = {
  generate: 'generated',
  prompt: 'prompt',
  manual: 'manual edit',
  restore: 'restored',
};

export const ERROR_EN: Record<string, string> = {
  invalid_credentials: 'Invalid username or password',
  user_disabled: 'This account is disabled',
  user_not_found: 'User not found',
  bad_request: 'Invalid request',
  not_found: 'Not found',
  invalid_spec: 'The spec failed validation',
  generation_already_running:
    'A generation for this project is already running — wait for it to finish',
  no_source_content:
    'No source text: paste a call transcript, upload files, or add an extra instruction',
  invalid_inputs: 'The generation inputs failed validation',
  invalid_model: 'Unsupported model',
  invalid_name: 'Invalid project name',
  no_spec: 'Generate the poster first',
  no_files: 'No files selected',
  render_failed: 'The engine could not render the spec',
  llm_failed: 'The LLM did not return a valid spec — try again',
};

/* ── run-status free-text (DESIGN15 §F2.1) ─────────────────────────
   Two backend run messages arrive as free text on run.error rather than as a
   mappable error CODE: the stale-run SWEEP («Generation lost») and the
   operator STOP. DESIGN15 made both English + white-label, but a run started
   BEFORE the round deployed can still carry the legacy Russian text. Every
   surface renders run.error directly, so normalise those two here: map either
   language to the canonical English. Any OTHER run.error is already clean
   English from the backend (the provider-error classifier) and passes through
   unchanged. Exported as the single source the panels + TMA reuse — no fork. */
const RUN_MESSAGE_EN: Record<string, string> = {
  // operator stop (the cancel routes)
  'Stopped by the operator.': 'Stopped by the operator.',
  'Остановлено оператором.': 'Stopped by the operator.',
  // stale-run sweep (note: the legacy Russian carried no trailing period)
  'Generation lost — start it again.': 'Generation lost — start it again.',
  'Генерация потеряна — запустите ещё раз': 'Generation lost — start it again.',
};

/** Normalise a backend run message to clean English. Known sweep/stop text
    (English or legacy Russian) maps to its canonical English; anything else —
    already-English provider text or empty — is returned trimmed as-is. */
export function mapKnownRunMessage(raw: string | null | undefined): string {
  const t = (raw || '').trim();
  return RUN_MESSAGE_EN[t] || t;
}

export type Kind =
  | 'meta'
  | 'legend'
  | 'block'
  | 'case'
  | 'final'
  | 'node'
  | 'lane'
  | 'table'
  | 'tableRow';

export const KIND_LABEL: Record<Kind, string> = {
  meta: 'Poster header',
  legend: 'Legend',
  block: 'Legend block',
  case: 'Case',
  final: 'Final block',
  node: 'Node',
  lane: 'Lane',
  table: 'Table',
  tableRow: 'Table row',
};

/* ── generic helpers ───────────────────────────────────── */

export function deepCopy<T>(o: T): T {
  return typeof structuredClone === 'function'
    ? structuredClone(o)
    : JSON.parse(JSON.stringify(o));
}

export function swap(arr: any[], i: number, j: number): void {
  const t = arr[i];
  arr[i] = arr[j];
  arr[j] = t;
}

export function ensureArr(obj: any, key: string): any[] {
  if (!Array.isArray(obj[key])) {
    // a string instead of an array — wrap it, keeping the content
    obj[key] = typeof obj[key] === 'string' && obj[key] !== '' ? [obj[key]] : [];
  }
  return obj[key];
}

export function autoGrow(ta: HTMLTextAreaElement): void {
  ta.style.height = 'auto';
  ta.style.height = ta.scrollHeight + 2 + 'px';
}

export function fmtK(n: number): string {
  if (!n) return '0';
  if (n < 1000) return String(n);
  return (n / 1000).toFixed(n < 10000 ? 1 : 0) + 'k';
}

export function fmtSize(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

/* Dates, everywhere on the architect surfaces and the mini app (DESIGN44).
   The team reads them in the US: month first, then day, 12-hour clock, and
   ALWAYS Eastern time with the zone spelled out («07/17, 12:55 PM EDT») —
   Dana: «сделать дату американского формата, сначала месяц, потом день; они
   тут все запутаются; убедиться, что время стоит EST». The zone is pinned
   to America/New_York rather than the viewer's device, so two people on two
   continents read the same stamp. */
export const DISPLAY_TIME_ZONE = 'America/New_York';

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    timeZone: DISPLAY_TIME_ZONE,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/** Date only, same order and zone («07/17»). */
export function fmtDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { timeZone: DISPLAY_TIME_ZONE, month: '2-digit', day: '2-digit' });
}

export function fmtElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

export function slugName(name: string | null | undefined): string {
  // the U+0400-U+04FF range keeps Cyrillic project names intact
  const s = (name || 'poster')
    .replace(new RegExp("[^\\w\\u0400-\\u04FF-]+", "g"), "_")
    .replace(/^_+|_+$/g, '');
  return (s || 'poster') + '.pdf';
}

/* ── paths ─────────────────────────────────────────────── */

export function pathKind(path: string): Kind | null {
  if (path === 'meta') return 'meta';
  if (path === 'legend') return 'legend';
  if (/^legend\.blocks\.\d+$/.test(path)) return 'block';
  if (/^cases\.\d+$/.test(path)) return 'case';
  if (/^cases\.\d+\.final$/.test(path)) return 'final';
  if (/^cases\.\d+\.nodes\.\d+$/.test(path)) return 'node';
  if (/^cases\.\d+\.lanes\.\d+\.nodes\.\d+$/.test(path)) return 'node';
  if (/^cases\.\d+\.lanes\.\d+$/.test(path)) return 'lane';
  if (/^tables\.\d+\.rows\.\d+$/.test(path)) return 'tableRow';
  if (/^tables\.\d+$/.test(path)) return 'table';
  return null;
}

export function resolvePath(obj: any, path: string): { value: any } | null {
  if (!obj || !path) return null;
  const segs = path.split('.');
  let cur: any = obj;
  for (const s of segs) {
    if (cur == null) return null;
    const key = /^\d+$/.test(s) ? parseInt(s, 10) : s;
    cur = cur[key];
  }
  return { value: cur };
}

export function selectionTitle(kind: Kind | null, value: any): string {
  let label = (kind && KIND_LABEL[kind]) || 'Element';
  if (kind === 'node' && value && value.id) label += ' [' + value.id + ']';
  if (kind === 'lane' && value && value.tag) label += ' ' + value.tag;
  return label;
}

/* ── defaults for new elements ─────────────────────────── */

export function defaultNode(): any {
  return { type: 'box', title: ['New node'], desc: [] };
}
export function defaultLane(): any {
  return {
    tag: '',
    title: 'NEW LANE',
    color: 'teal',
    role: 'stream',
    fan_label: '',
    intro: [],
    nodes: [defaultNode()],
  };
}
export function defaultFinal(): any {
  return { title: ['Final block'], paras: [], items: [], tail: '', color: 'blue' };
}
export function defaultBranch(): any {
  return { label: 'branch', color: 'eblue', lines: [''] };
}
export function defaultChip(): any {
  return { lines: ['CASE: …'], color: 'purple' };
}
export function defaultBlock(): any {
  return { title: 'New block', bullets: [''] };
}

export function nodeTitlePreview(n: any, i: number): string {
  const t = Array.isArray(n.title) ? n.title.join(' ') : String(n.title || '');
  return t || 'Node ' + (i + 1);
}

/* ═══════════ cleanSpec / cleanNode (ported verbatim) ═══════════ */

function cleanStrArr(a: any): any {
  if (!Array.isArray(a)) return a;
  return a
    .map((x) => (typeof x === 'string' ? x.trim() : x))
    .filter((x) => x !== '');
}

function cleanNode(n: any): void {
  if (!n || typeof n !== 'object') return;
  if (Array.isArray(n.title)) n.title = cleanStrArr(n.title);
  if (Array.isArray(n.desc)) n.desc = cleanStrArr(n.desc);
  if (n.id === '') delete n.id;
  if (n.next_label === '') delete n.next_label;
  if (Array.isArray(n.apis)) {
    n.apis = n.apis.filter(
      (a: any) => a && ((a.service || '').trim() || (a.purpose || '').trim()),
    );
    if (!n.apis.length) delete n.apis;
  }
  if (n.loop) {
    // round 21: prune empty routing keys (mirror the phase-field pattern),
    // but keep a loop that carries only a target/label alive.
    if (typeof n.loop.to === 'string' && !n.loop.to.trim()) delete n.loop.to;
    if (typeof n.loop.branch_label === 'string' && !n.loop.branch_label.trim())
      delete n.loop.branch_label;
    if (
      !(n.loop.label || '').trim() &&
      !(n.loop.note || '').trim() &&
      n.loop.to == null &&
      n.loop.branch_label == null
    )
      delete n.loop;
  }
  if (n.type !== 'decision') {
    delete n.next_label;
    delete n.loop;
  }
  if (n.type === 'fork') {
    if (Array.isArray(n.branches)) {
      for (const b of n.branches) {
        if (Array.isArray(b.lines)) b.lines = cleanStrArr(b.lines);
      }
    }
  } else {
    delete n.branches;
  }
}

/** Careful cleanup of the working copy before PUT: empty bullet lines
    and empty optional fields are dropped, the structure is unchanged. */
export function cleanSpec(src: any): any {
  const s = deepCopy(src);
  if (s.meta) {
    if (s.meta.footer === '') delete s.meta.footer;
    if (s.meta.subtitle === '') delete s.meta.subtitle;
    // Optional phase header (DESIGN19): keep the keys when set, but drop
    // an emptied field so the poster block disappears — mirrors subtitle.
    if (s.meta.phase_title === '') delete s.meta.phase_title;
    if (s.meta.phase_summary === '') delete s.meta.phase_summary;
  }
  if (s.legend) {
    const lg = s.legend;
    if (lg.user && !(lg.user.label || '').trim() && !(lg.user.sublabel || '').trim())
      delete lg.user;
    if (Array.isArray(lg.dashboard)) lg.dashboard = cleanStrArr(lg.dashboard);
    if (Array.isArray(lg.nucleus)) {
      lg.nucleus = cleanStrArr(lg.nucleus);
      if (!lg.nucleus.length) lg.nucleus = null;
    }
    if (Array.isArray(lg.case_chips)) {
      for (const chip of lg.case_chips) {
        if (Array.isArray(chip.lines)) chip.lines = cleanStrArr(chip.lines);
      }
      lg.case_chips = lg.case_chips.filter(
        (c: any) => Array.isArray(c.lines) && c.lines.length,
      );
      if (!lg.case_chips.length) delete lg.case_chips;
    }
    if (Array.isArray(lg.blocks)) {
      for (const b of lg.blocks) {
        if (Array.isArray(b.bullets)) b.bullets = cleanStrArr(b.bullets);
      }
    }
  }
  if (Array.isArray(s.cases)) {
    for (const c of s.cases) {
      if (Array.isArray(c.intro)) c.intro = cleanStrArr(c.intro);
      if (Array.isArray(c.nodes)) c.nodes.forEach(cleanNode);
      if (Array.isArray(c.lanes)) {
        for (const l of c.lanes) {
          if (Array.isArray(l.intro)) l.intro = cleanStrArr(l.intro);
          if (l.fan_label === '') delete l.fan_label;
          if (l.exception_label === '') delete l.exception_label;
          if ((l.role || 'stream') === 'stream') delete l.exception_label;
          if (Array.isArray(l.nodes)) l.nodes.forEach(cleanNode);
        }
        if (!c.lanes.length) delete c.lanes;
      }
      if (c.final) {
        const f = c.final;
        if (Array.isArray(f.title)) f.title = cleanStrArr(f.title);
        if (Array.isArray(f.paras)) {
          f.paras = cleanStrArr(f.paras);
          if (!f.paras.length) delete f.paras;
        }
        if (Array.isArray(f.items)) {
          f.items = cleanStrArr(f.items);
          if (!f.items.length) delete f.items;
        }
        if (f.tail === '') delete f.tail;
      }
    }
  }
  if (Array.isArray(s.tables)) {
    for (const t of s.tables) {
      if (Array.isArray(t.rows)) {
        t.rows = t.rows.map((r: any) =>
          Array.isArray(r)
            ? r.map((cell: any) => (typeof cell === 'string' ? cell.trim() : cell))
            : r,
        );
      }
    }
  }
  return s;
}
